// Pushes queued sales to the server, one at a time, oldest first.
//
// Order matters more than throughput. Sales carry pre-printed receipt numbers and
// decrement shared stock, so pushing them out of order makes the books read
// strangely for no gain — a shop generates sales at human speed, and the queue is
// normally one deep for a second or two.
//
// Everything about failure handling lives in queue.ts's verdictFor; this module
// decides only WHEN to try.

import { backoffMs, confirm, nextPending, recordFailure, type PendingSale } from './queue'
import { withLock } from './lock'

export interface DrainDeps {
  /** The namespace to drain, or null when nobody is signed in. */
  ns: () => string | null
  /** Push one sale. Resolves on success; rejects with an error carrying `.code`. */
  push: (sale: PendingSale) => Promise<void>
}

export interface Drain {
  /** Try now (a new sale, a reconnect, a tab becoming visible). Safe to call often. */
  kick: () => void
  stop: () => void
}

function errorCode(err: unknown): string {
  const code = (err as { code?: unknown })?.code
  return typeof code === 'string' ? code : 'NETWORK'
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export function startDrain(deps: DrainDeps): Drain {
  let timer: ReturnType<typeof setTimeout> | null = null
  let running = false
  let stopped = false
  let consecutiveFailures = 0

  const schedule = (ms: number): void => {
    if (stopped || timer) return
    timer = setTimeout(() => {
      timer = null
      void cycle()
    }, ms)
  }

  const cycle = async (): Promise<void> => {
    if (stopped || running) return
    const ns = deps.ns()
    if (!ns) return
    running = true
    try {
      // 'skip': another tab already draining means there is nothing for us to do.
      // Not a correctness guard — checkout is idempotent on the sale id, so the
      // loser of a race gets the same sale back — but it halves the requests and
      // keeps the ordering sane.
      await withLock(`tendally-drain-${ns}`, 'skip', async () => {
        for (;;) {
          if (stopped) return
          const sale = nextPending(ns)
          if (!sale) {
            consecutiveFailures = 0
            return
          }
          try {
            await deps.push(sale)
            await confirm(sale.id)
            consecutiveFailures = 0
          } catch (err) {
            await recordFailure(sale.id, errorCode(err), errorMessage(err))
            consecutiveFailures++
            // Stop the pass rather than move on to the next sale. Whatever blocked
            // this one — no network, a dead session, a lapsed licence — will almost
            // certainly block the next, and hammering the server while offline
            // wastes battery for nothing. Except a sale that just went `failed`:
            // that one is out of the way, so the queue can keep moving.
            if (nextPending(ns)?.id === sale.id) return
          }
        }
      })
    } finally {
      running = false
      // Only re-arm while there is work. An empty queue costs no timers at all.
      if (deps.ns() && nextPending(deps.ns()!)) schedule(backoffMs(consecutiveFailures))
    }
  }

  const kick = (): void => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    void cycle()
  }

  // The three moments a stuck queue is most likely to become unstuck. `online` is
  // the honest one; `visibilitychange` catches the laptop lid, which fires no
  // network event at all.
  const onOnline = (): void => kick()
  const onVisible = (): void => {
    if (!document.hidden) kick()
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
  }

  return {
    kick,
    stop() {
      stopped = true
      if (timer) clearTimeout(timer)
      timer = null
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline)
        document.removeEventListener('visibilitychange', onVisible)
      }
    }
  }
}
